import { useEffect, useState } from "react";
import { Release } from "@packages/commons";
import FloatingPanel from "@imminently/decisively-ui/FloatingPanel";
import Popover from "@imminently/decisively-ui/Popover";
import Button from "@imminently/decisively-ui/Button";
import Tooltip from "@imminently/decisively-ui/Tooltip";
import Avatar from "@imminently/decisively-ui/Avatar";
import Icon from "@imminently/decisively-ui/Icon";
import Dialog from "@imminently/decisively-ui/Dialog";
import { type BackendResource, filterAction } from "@imminently/immi-query";
import * as fullReleaseRedux from "@/redux/fullRelease/reducer";
import { documentService, releaseService, tasksService } from "@/app/providers/services";
import { useAuth, crudGetOne } from "@/legacy/redux";
import { useDispatch, useSelector } from "react-redux";
import { useFullRelease } from "@/common/hooks_useFullRelease";
import { useNotify } from "@/common/notifications";
import { filter } from "rxjs";
import { cn } from "@/lib/utils";
import Chip, { ChipProps } from "@imminently/decisively-ui/Chip";
import styles from "./ReleaseNotifications.module.scss";
import {
  buildReleaseNotificationFeed,
  getJobQueueStartedAt,
  getReleaseJobDisplayStatus,
  getReleaseJobTimingText,
  getVisibleTrackedJobsAhead,
  isReleaseJobNotification,
  isTaskNotification,
  type LiveJobUpdate,
  type ReleaseNotificationFeedItem,
  type ReleaseNotificationJob,
  type ReleaseNotificationTask,
} from "./ReleaseNotifications.utils";

export interface ReleaseNotificationsProps {
  release: BackendResource<Release> | null;
}

// very naughty having a module level variable, but it's a quick way to prevent duplicate notifications
const received: string[] = [];

const getMessageKey = (message: any) => {
  const result = message.data.result;
  return `${message.data.releaseId}-${result.id}-${result.status}-${result.processingStartedAt || result.processingFinishedAt || result.updated || ""}`;
};

const formatNotificationDueDate = (dueDate: string | undefined) => {
  if (!dueDate) {
    return undefined;
  }
  const parsed = new Date(dueDate);
  return Number.isNaN(parsed.getTime()) ? dueDate : parsed.toLocaleDateString();
};

const useReleaseJobs = () => {
  const dispatch = useDispatch();
  const notify = useNotify();
  const { user } = useAuth();

  const release = useFullRelease();
  const [liveJobUpdates, setLiveJobUpdates] = useState<Record<string, LiveJobUpdate>>({});

  const refresh = () => {
    if (release?.id) {
      // update release as graph should have changed
      // updates legacy release, which uses old immi platform
      dispatch(crudGetOne("releases", release.id));
      // upates full release, which uses new query system
      dispatch(fullReleaseRedux.aCreators.requestToActualize());
    }
  };

  const notifications = buildReleaseNotificationFeed(release, liveJobUpdates);
  const jobs = notifications.filter(isReleaseJobNotification);
  const inProgress = jobs.some((job) => job.status === "queued");
  const documentsCompiling = {};
  const documentsWithErrors = {};

  // @ts-ignore - documents exists in the store, we just haven't typed it
  const editingDocument = useSelector(s => s.documents?.document);

  for (const job of jobs) {
    if (job.documentId && job.type === "compileDocument") {
      if (job.status === "queued") {
        if (!documentsCompiling[job.documentId]) {
          documentsCompiling[job.documentId] = true;
        }
        // @ts-ignore
      } else if (job.status === "failed" || job.status === "error") {
        documentsWithErrors[job.documentId] = true;
      }
    }
  }

  const releaseId = release?.id; // use this so that the effect only runs when the release changes
  useEffect(() => {
    if (releaseId) {
      const sub = releaseService.pubsub$
        .pipe(
          filterAction("release-job-update"),
          // filter out any jobs we've already received
          filter((msg: any) => msg.data.result && !received.includes(getMessageKey(msg)))
        )
        .subscribe((msg) => {
          const result = msg.data.result;
          const jobId: string = result.id;
          received.push(getMessageKey(msg)); // add to list of received jobs
          // notify.info(`Job ${jobId} updated, type: ${result.type}, status: ${result.status}`);
          if (msg.data.releaseId === releaseId) {
            setLiveJobUpdates((current) => ({
              ...current,
              [jobId]: {
                ...current[jobId],
                ...result,
                releaseId: msg.data.releaseId,
              },
            }));
            // update release as graph should have changed
            // updates legacy release, which uses old immi platform
            dispatch(crudGetOne("releases", releaseId));
            // upates full release, which uses new query system
            dispatch(fullReleaseRedux.aCreators.requestToActualize());
            const jobType = result.type;
            if (jobType === "compileDocument") {
              const documentId = result.documentId;
              // only alert them if they are the one who compiled
              if (documentId && msg.data.author === user.id) {
                documentService.invalidateAllQueries();
                if (result.status === "failed") {
                  notify.error(`Something went wrong compiling '${result.title}', please try again`);
                } else if (result.errors?.length) {
                  notify.error(`Compiled '${result.title}' with ${result.errors.length} error${result.errors.length !== 1 ? "s" : ""}`);
                } else if (result.status === "completed") {
                  notify.info(`Compiled '${result.title}'`);
                }
              }
            } else if (jobType === "add-entity") {
              if (msg.data.author === user.id) {
                if (editingDocument) {
                  documentService.invalidateAllQueries();
                }
              }
            }
          }
        });
      return () => {
        sub.unsubscribe();
      };
    }
  }, [releaseId]);

  return { notifications, jobs, inProgress, documentsWithErrors, refresh };
};

const jobStatusToChipVariant = (job: ReleaseNotificationJob): ChipProps["variant"] => {
  if (job.status === "queued" && job.processingStartedAt) {
    return "info";
  }
  switch (job.status) {
    case "completed":
      return "success";
    case "failed":
      return "destructive";
    case "queued":
      return "info";
    default:
      return undefined;
  }
};

const notificationSeverityToChipVariant = (notification: ReleaseNotificationTask): ChipProps["variant"] => {
  switch (notification.severity) {
    case "critical":
      return "destructive";
    case "warning":
      return "warning";
    default:
      return "info";
  }
};

const NotificationDetailModal = ({
  notification,
  onClose,
  onMarkRead,
  onDismiss,
  updating,
}: {
  notification: ReleaseNotificationTask | null;
  onClose: () => void;
  onMarkRead: (notification: ReleaseNotificationTask) => void;
  onDismiss: (notification: ReleaseNotificationTask) => void;
  updating: boolean;
}) => {
  const dueDate = notification ? formatNotificationDueDate(notification.dueDate) : undefined;

  return (
    <Dialog
      open={Boolean(notification)}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      {notification && (
        <Dialog.Content className="flex w-full max-w-lg flex-col">
          <Dialog.Header>
            <Dialog.Title>{notification.subject || notification.title}</Dialog.Title>
            <Dialog.Controls />
          </Dialog.Header>
          <Dialog.Body className="flex flex-col gap-4 text-sm">
            {dueDate && (
              <div className="text-xs font-medium text-gray-600">
                Due {dueDate}
              </div>
            )}
            <p className="whitespace-pre-wrap text-gray-800">{notification.body}</p>
          </Dialog.Body>
          <Dialog.Actions>
            <Button
              label="Dismiss"
              variant="ghost"
              intent="secondary"
              loading={updating}
              onClick={() => onDismiss(notification)}
            >
              Dismiss
            </Button>
            {notification.notificationStatus !== "read" && (
              <Button
                label="Mark as read"
                intent="primary"
                loading={updating}
                onClick={() => onMarkRead(notification)}
              >
                Mark as read
              </Button>
            )}
          </Dialog.Actions>
        </Dialog.Content>
      )}
    </Dialog>
  );
};

const getNotificationCount = (notifications: ReleaseNotificationFeedItem[]) => {
  return notifications.filter((notification) => {
    if (isReleaseJobNotification(notification)) {
      return notification.status === "queued";
    }
    return notification.notificationStatus === "unread";
  }).length;
};

export const ReleaseNotifications = () => {
  const [menuVisible, setMenuVisible] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [selectedNotification, setSelectedNotification] = useState<ReleaseNotificationTask | null>(null);
  const [updatingNotificationId, setUpdatingNotificationId] = useState<string | null>(null);
  const { notifications, jobs, inProgress, documentsWithErrors, refresh } = useReleaseJobs();
  const notify = useNotify();

  const errors = Object.keys(documentsWithErrors).length;
  const count = getNotificationCount(notifications);
  const hasErrors = errors > 0;

  const showBadge = count > 0 || hasErrors;

  const updateNotificationStatus = (notification: ReleaseNotificationTask, notificationStatus: "read" | "archived") => {
    setUpdatingNotificationId(notification.id);
    tasksService.update({
      id: notification.id,
      payload: { notificationStatus } as any,
    })
      .then(() => {
        if (notificationStatus === "archived") {
          setSelectedNotification((current) => current?.id === notification.id ? null : current);
        } else {
          setSelectedNotification((current) => current?.id === notification.id ? {
            ...current,
            notificationStatus,
          } : current);
        }
        refresh();
      })
      .catch((error) => {
        notify.error("Could not update notification");
        console.error(error);
      })
      .finally(() => setUpdatingNotificationId(null));
  };

  useEffect(() => {
    if (!menuVisible || count === 0) {
      return;
    }
    setNow(Date.now());
    const intervalId = setInterval(() => setNow(Date.now()), 1000 * 15);
    return () => clearInterval(intervalId);
  }, [menuVisible, count]);

  return (
    <Popover
      visible={menuVisible}
      onVisibleChange={(visible) => {
        setMenuVisible(visible);
        if (visible) refresh();
      }}
      mode="click"
      placement="bottom-end"
      offset={8}
      interactable
    >
      {(popover) => (
        <>
          <Popover.Reference asChild>
            <Button
              label="Notifications"
              tooltip="Notifications"
              variant="ghost"
              intent="sidebar"
              size="sm"
              className={cn("relative h-8 w-8 p-0", inProgress ? styles.loading : "")}
            >
              <Icon name="notification" />
              {showBadge && (
                <span className={cn(
                  "absolute top-px right-px h-2 w-2 rounded-full",
                  hasErrors && count === 0 ? "bg-red-500" : "bg-primary"
                )} />
              )}
            </Button>
          </Popover.Reference>
          <Popover.Content
            asChild
            handleVisibility={false}
          >
            <FloatingPanel
              open={popover.visible}
              placement={popover.placement}
              unmountWhenHidden
              className="flex max-h-[min(32rem,calc(100vh-5rem))] min-h-25 w-87.5 max-w-100 flex-col overflow-hidden p-2"
            >
              <h4 className="shrink-0 text-sm font-semibold">Notifications</h4>
              <ul className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto pt-2">
                {notifications.length > 0 ? (
                  notifications.map((notification, i) => {
                    if (isTaskNotification(notification)) {
                      const isUpdating = updatingNotificationId === notification.id;
                      return (
                        <li key={notification.id || i} className="flex w-full items-center gap-2 text-normal p-3 border rounded-md">
                          <button
                            type="button"
                            className="min-w-0 flex-1 cursor-pointer text-left"
                            onClick={() => setSelectedNotification(notification)}
                          >
                            <span className={cn(
                              "block truncate text-sm",
                              notification.notificationStatus === "unread" ? "font-semibold" : "font-medium",
                            )}>
                              {notification.subject || notification.title}
                            </span>
                          </button>
                          <div className="flex shrink-0 items-center gap-1">
                            <Chip variant={notificationSeverityToChipVariant(notification)} className={"text-xs text-gray-600"}>
                              {notification.severity}
                            </Chip>
                            <Button
                              icon
                              size="sm"
                              label="Mark as read"
                              disabled={isUpdating || notification.notificationStatus === "read"}
                              onClick={(event) => {
                                event.stopPropagation();
                                updateNotificationStatus(notification, "read");
                              }}
                            >
                              <Icon name="check" />
                            </Button>
                            <Button
                              icon
                              size="sm"
                              label="Dismiss"
                              disabled={isUpdating}
                              onClick={(event) => {
                                event.stopPropagation();
                                updateNotificationStatus(notification, "archived");
                              }}
                            >
                              <Icon name="close" />
                            </Button>
                          </div>
                        </li>
                      );
                    }

                    const displayJob = {
                      ...notification,
                      trackedJobsAhead: getVisibleTrackedJobsAhead(notification, jobs),
                    };
                    return (
                      <li key={notification.id || i} className="flex w-full flex-col gap-1 text-normal p-4 border rounded-md">
                        <div className="flex w-full justify-between">
                          <span className={"text-xs text-gray-600"}>
                            {displayJob.description}
                          </span>

                          <Chip variant={jobStatusToChipVariant(displayJob)} className={"text-xs text-gray-600"}>
                            {getReleaseJobDisplayStatus(displayJob)}
                          </Chip>
                        </div>
                        <Tooltip>
                          <span className={"block text-wrap text-sm font-medium"} title={displayJob.title || displayJob.documentName}>
                            {displayJob.title}
                          </span>
                          <Tooltip.Content placement="bottom">
                            {displayJob.title || displayJob.documentName}
                          </Tooltip.Content>
                        </Tooltip>
                        <div className="flex w-full items-center justify-between text-[12px]">
                          <Tooltip>
                            <div>
                              {displayJob.started && getReleaseJobTimingText(displayJob, now)}
                            </div>
                            <Tooltip.Content placement="right">
                              Queued {new Date(getJobQueueStartedAt(displayJob)).toLocaleString()}
                            </Tooltip.Content>
                          </Tooltip>
                          <Tooltip>
                            <Avatar
                              name={displayJob.user?.full_name}
                              size="sm"
                            />
                            <Tooltip.Content placement="left">
                              {displayJob.user?.full_name}
                            </Tooltip.Content>
                          </Tooltip>
                        </div>
                      </li>
                    );
                  })
                ) : (
                  <div className="flex items-center justify-center py-8 text-gray-500">
                    <i>No notifications</i>
                  </div>
                )}
              </ul>
            </FloatingPanel>
          </Popover.Content>
          <NotificationDetailModal
            notification={selectedNotification}
            onClose={() => setSelectedNotification(null)}
            onMarkRead={(notification) => updateNotificationStatus(notification, "read")}
            onDismiss={(notification) => updateNotificationStatus(notification, "archived")}
            updating={Boolean(selectedNotification && updatingNotificationId === selectedNotification.id)}
          />
        </>
      )}
    </Popover>
  );
};
